"use client";

import { useState } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconTarget from "@/app/(components)/generics/Icons/IconTarget/IconTarget";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import {
  ProspectCriteriaField,
  ProspectSearchCriteria,
} from "@/app/types/prospect/ProspectSearchCriteria";
import { UDate } from "@/app/utils/UDate";
import { Limits } from "@/app/vars/Limits";
import {
  hasCriteriaData,
  PROSPECT_STRATEGY_GROUPS,
  splitAvatarUpdatedMessage,
} from "./ProspectStrategy.logic";
import styles from "./ProspectStrategy.module.css";
import ProspectStrategyGroupPanel from "./ProspectStrategyGroup";

type Props = {
  lang: string;
  criteria?: ProspectSearchCriteria | null;
  isPlaceholder?: boolean;
  hasCriteria?: boolean;
  needUpdate?: boolean;
  avatarUpdatedAt?: string | null;
  isGenerating?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  onGenerate?: () => void;
  onRegenerate?: () => void;
  onSaveField?: (field: ProspectCriteriaField, items: string[]) => void;
};

export default function ProspectStrategy(props: Props) {
  const T = useT();
  const {
    lang,
    criteria,
    isPlaceholder = false,
    hasCriteria = false,
    needUpdate = false,
    avatarUpdatedAt,
    isGenerating = false,
    isLoading = false,
    isOpen: isOpenProp,
    onToggle,
    onGenerate,
    onRegenerate,
    onSaveField,
  } = props;

  const [internalOpen, setInternalOpen] = useState(true);
  const isOpen = isOpenProp ?? internalOpen;

  const toggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalOpen((prev) => !prev);
    }
  };

  const hasData = !isPlaceholder && hasCriteriaData(criteria);
  const showColumns = isPlaceholder || hasData;
  const fieldSaver = hasData ? onSaveField : undefined;

  const showUpdateBanner =
    !isPlaceholder && hasCriteria && needUpdate && !!onRegenerate && !isGenerating;

  const [updatedBefore, updatedAfter] = splitAvatarUpdatedMessage(
    String(T(TKey.PROSPECT_SEARCH_AVATAR_UPDATED)),
  );

  // Editable and placeholder modes always show every tab; the read-only view
  // hides a group whose fields are all empty so no orphan tab is left.
  const visibleGroups = PROSPECT_STRATEGY_GROUPS.filter((group) => {
    if (isPlaceholder || fieldSaver) {
      return true;
    }
    return group.categories.some((category) => (criteria?.[category.field]?.length ?? 0) > 0);
  });

  return (
    <div className={styles.card}>
      <div className={styles.header} onClick={toggle}>
        <div className={styles.headerLeft}>
          <span className={styles.iconWrapper}>
            <IconTarget size={26} />
          </span>
          <h2 className={styles.title}>{T(TKey.PROSPECT_SEARCH_STRATEGY_TITLE)}</h2>
        </div>
        <span className={styles.chevron}>{isOpen ? <IconArrowUp /> : <IconArrowDown />}</span>
      </div>

      {showUpdateBanner && (
        <div className={styles.updateBanner}>
          <p className={styles.updateText}>
            {updatedBefore}
            {avatarUpdatedAt && (
              <span className={styles.updateAgo}>
                {" "}
                ({UDate.timeAgo(new Date(avatarUpdatedAt), lang)})
              </span>
            )}
            {updatedAfter}
          </p>
          <Button
            text={T(TKey.PROSPECT_SEARCH_REGENERATE_CRITERIA_CTA)}
            type={ButtonType.PRIMARY}
            isLink={false}
            onClick={onRegenerate}
            min={true}
          />
        </div>
      )}

      {isOpen && isGenerating && (
        <div className={styles.generating}>
          <CircleLoader size={28} color="var(--color-green-3)" strokeWidth={3} />
          <p className={styles.generatingText}>{T(TKey.PROSPECT_SEARCH_GENERATING)}</p>
        </div>
      )}

      {isOpen && !isGenerating && isLoading && (
        <div className={styles.loading}>
          <PointsLoader color="var(--color-green-3)" delay={Limits.PROSPECT_SEARCH_LOADER_DELAY} />
        </div>
      )}

      {isOpen && !isGenerating && !isLoading && (
        <>
          {showColumns ? (
            <div className={styles.tabs}>
              <TabbedContent
                disableScrollOnTabChange={true}
                items={visibleGroups.map((group) => ({
                  id: group.titleKey,
                  title: T(group.titleKey) as string,
                  content: (
                    <ProspectStrategyGroupPanel
                      group={group}
                      lang={lang}
                      criteria={criteria}
                      isPlaceholder={isPlaceholder}
                      onSaveField={fieldSaver}
                    />
                  ),
                }))}
              />
            </div>
          ) : (
            <p className={styles.description}>
              {T(
                onGenerate
                  ? TKey.PROSPECT_SEARCH_CRITERIA_DESCRIPTION
                  : TKey.PROSPECT_SEARCH_CRITERIA_DESCRIPTION_MEMBER,
              )}
            </p>
          )}

          {!showColumns && !hasData && onGenerate && (
            <div className={styles.generate}>
              <Button
                text={T(TKey.PROSPECT_SEARCH_GENERATE_CRITERIA_CTA)}
                type={ButtonType.PRIMARY}
                isLink={false}
                onClick={onGenerate}
                min={true}
              />
            </div>
          )}

          {showColumns && (
            <div className={styles.collapseBottom}>
              {!hasData && onGenerate && (
                <Button
                  text={T(TKey.PROSPECT_SEARCH_GENERATE_CRITERIA_CTA)}
                  type={ButtonType.PRIMARY}
                  isLink={false}
                  onClick={onGenerate}
                  min={true}
                />
              )}
              <button type="button" className={styles.collapseBtn} onClick={toggle}>
                <span>{T(TKey.PROSPECT_SEARCH_COLLAPSE)}</span>
                <IconArrowUp />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
